import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { InlineEdit } from '@/components/ui/inline-edit'
import { EditableDropdown } from '@/components/ui/editable-dropdown'
import { Checkbox } from '@/components/ui/checkbox'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useMeetingUpdate } from '@/hooks/useMeetingUpdate'
import { useClients } from '@/hooks/useClients'
import { cn } from '@/lib/utils'
import { 
  Calendar, 
  Mail, 
  Building2, 
  User, 
  Clock, 
  Target, 
  ExternalLink,
  Edit3,
  Check,
  X,
  Trash2,
  StickyNote,
  Upload,
  FileImage,
  Link,
  CalendarIcon
} from 'lucide-react'
import { format } from 'date-fns'
import { toast } from '@/hooks/use-toast'
import { MeetingNotesScreenshots } from './MeetingNotesScreenshots'

interface MeetingDetailViewProps {
  isOpen: boolean
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
  meeting: {
    id: string
    client: string
    client_id: number
    lead_email: string
    email_domain: string
    campaign_name?: string
    meetings_booked_create_date: string
    source: string
    attribution_days: number
    direct_meeting: boolean
    lead_status?: string
    sf_account_id?: string
    email_sent_time?: string
    op_created_date?: string
    created_at: string
    updated_at: string
    notes?: string
    is_meeting_booked?: boolean
    meeting_booked_date?: string
    is_qualified_meeting?: boolean
    qualified_meeting_date?: string
  } | null
}

interface EditableFieldProps {
  label: string
  value: string
  onSave: (value: string) => void
  multiline?: boolean
  type?: string
  className?: string
}

const EditableField = ({ label, value, onSave, multiline = false, type = "text", className = "" }: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')

  const handleSave = () => {
    onSave(editValue)
    setIsEditing(false)
    toast({
      title: "Field Updated",
      description: `${label} has been updated successfully.`,
    })
  }

  const handleCancel = () => {
    setEditValue(value || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className={`space-y-2 ${className}`}>
        <label className="text-sm font-medium text-muted-foreground">{label}</label>
        <div className="flex items-center gap-2">
          {multiline ? (
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
              rows={3}
              autoFocus
            />
          ) : (
            <Input
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
              autoFocus
            />
          )}
          <Button size="sm" variant="outline" onClick={handleSave}>
            <Check className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={`group cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors ${className}`} onClick={() => setIsEditing(true)}>
      <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        {label}
        <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
      </label>
      <p className={`text-sm ${multiline ? 'whitespace-pre-wrap' : ''} ${!value ? 'text-muted-foreground italic' : ''}`}>
        {value || 'Click to add...'}
      </p>
    </div>
  )
}

const NotesWithUpload = ({ value, onSave }: { value: string; onSave: (value: string) => void }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')
  const [uploadedImages, setUploadedImages] = useState<string[]>([])

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      Array.from(files).forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const imageUrl = e.target?.result as string
          setUploadedImages(prev => [...prev, imageUrl])
          setEditValue(prev => prev + `\n![Screenshot](${imageUrl})`)
        }
        reader.readAsDataURL(file)
      })
    }
  }

  const handleSave = () => {
    onSave(editValue)
    setIsEditing(false)
    toast({
      title: "Notes Updated",
      description: "Meeting notes have been updated successfully.",
    })
  }

  const handleCancel = () => {
    setEditValue(value || '')
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-muted-foreground">Meeting Notes</label>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button size="sm" variant="outline" type="button">
                <Upload className="h-3 w-3 mr-1" />
                Add Screenshot
              </Button>
            </label>
          </div>
        </div>
        <Textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="min-h-[120px]"
          placeholder="Add meeting notes... You can also upload screenshots above."
          autoFocus
        />
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleSave}>
            <Check className="h-3 w-3 mr-1" />
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="group cursor-pointer hover:bg-muted/30 p-4 rounded-lg transition-colors border border-muted/40 hover:border-muted" onClick={() => setIsEditing(true)}>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          Meeting Notes
          <Edit3 className="h-4 w-4 opacity-0 group-hover:opacity-60 transition-opacity" />
        </label>
        <FileImage className="h-4 w-4 text-muted-foreground/60" />
      </div>
      <p className={`text-sm whitespace-pre-wrap leading-relaxed ${!value ? 'text-muted-foreground italic' : ''}`}>
        {value || 'Click to add notes and screenshots...'}
      </p>
    </div>
  )
}

export const MeetingDetailView = ({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  meeting
}: MeetingDetailViewProps) => {
  const [localMeeting, setLocalMeeting] = useState(meeting)
  const { mutate: updateMeeting } = useMeetingUpdate()
  const { data: clients = [] } = useClients()

  const meetingTypeOptions = [
    { value: 'true', label: 'Direct Meeting' },
    { value: 'false', label: 'Attribution Meeting' }
  ]

  const clientOptions = clients.map(client => ({
    value: client.name,
    label: client.name
  }))

  // Update local state when meeting prop changes
  React.useEffect(() => {
    setLocalMeeting(meeting)
  }, [meeting])

  if (!localMeeting) return null

  const handleFieldUpdate = async (field: string, value: string) => {
    // Update local state immediately
    setLocalMeeting(prev => prev ? { ...prev, [field]: value } : null)
    
    // Update in database
    updateMeeting({
      id: localMeeting.id,
      field,
      value
    })
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    } catch {
      return 'Invalid date'
    }
  }

  const getSourceBadgeVariant = (source: string) => {
    switch (source) {
      case 'manual':
        return 'secondary'
      case 'breakcold':
        return 'default'
      case 'sqls_meetings_booked':
        return 'outline'
      case 'merged':
        return 'destructive'
      case 'lead_category_updates':
        return 'default'
      default:
        return 'secondary'
    }
  }

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'manual':
        return 'Manual Entry'
      case 'breakcold':
        return 'Breakcold'
      case 'sqls_meetings_booked':
        return 'SQLS Attribution'
      case 'merged':
        return 'Merged Data'
      case 'lead_category_updates':
        return 'Lead Category Update'
      default:
        return source
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Meeting Details - {localMeeting.email_domain}
            </DialogTitle>
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">Click any field to edit it directly</p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Lead Email</label>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="font-mono text-sm">{localMeeting.lead_email}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email Domain</label>
                <div className="flex items-center gap-2 mt-1">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{localMeeting.email_domain}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Client</label>
                <div className="mt-1">
                  <EditableDropdown
                    value={localMeeting.client}
                    options={clientOptions}
                    onSave={(value) => handleFieldUpdate('client', value)}
                    placeholder="Select client"
                    allowCreate={true}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Meeting Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Meeting Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Meeting Date</label>
                <p className="text-sm mt-1">{formatDate(localMeeting.meetings_booked_create_date)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Source</label>
                <div className="mt-1">
                  <Badge variant={getSourceBadgeVariant(localMeeting.source)}>
                    {getSourceLabel(localMeeting.source)}
                  </Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Meeting Type</label>
                <div className="mt-1">
                  <EditableDropdown
                    value={localMeeting.direct_meeting ? 'true' : 'false'}
                    options={meetingTypeOptions}
                    onSave={(value) => handleFieldUpdate('direct_meeting', value)}
                    placeholder="Select meeting type"
                  />
                </div>
              </div>

              {localMeeting.attribution_days > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Attribution Days</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{localMeeting.attribution_days} days</span>
                  </div>
                </div>
              )}

              {/* Meeting Booked Checkbox */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="meeting-booked"
                    checked={localMeeting.is_meeting_booked || false}
                    onCheckedChange={(checked) => {
                      const isChecked = Boolean(checked)
                      handleFieldUpdate('is_meeting_booked', isChecked.toString())
                      if (!isChecked) {
                        // Clear the date when unchecking
                        handleFieldUpdate('meeting_booked_date', '')
                      }
                    }}
                  />
                  <label
                    htmlFor="meeting-booked"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Meeting Booked
                  </label>
                </div>
                
                {localMeeting.is_meeting_booked && (
                  <div className="ml-6">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-[240px] justify-start text-left font-normal",
                            !localMeeting.meeting_booked_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {localMeeting.meeting_booked_date
                            ? format(new Date(localMeeting.meeting_booked_date), "PPP")
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={localMeeting.meeting_booked_date ? new Date(localMeeting.meeting_booked_date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              handleFieldUpdate('meeting_booked_date', date.toISOString())
                            }
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>

              {/* Qualified Meeting Checkbox */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="qualified-meeting"
                    checked={localMeeting.is_qualified_meeting || false}
                    onCheckedChange={(checked) => {
                      const isChecked = Boolean(checked)
                      handleFieldUpdate('is_qualified_meeting', isChecked.toString())
                      if (!isChecked) {
                        // Clear the date when unchecking
                        handleFieldUpdate('qualified_meeting_date', '')
                      }
                    }}
                  />
                  <label
                    htmlFor="qualified-meeting"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Qualified Meeting
                  </label>
                </div>
                
                {localMeeting.is_qualified_meeting && (
                  <div className="ml-6">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-[240px] justify-start text-left font-normal",
                            !localMeeting.qualified_meeting_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {localMeeting.qualified_meeting_date
                            ? format(new Date(localMeeting.qualified_meeting_date), "PPP")
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={localMeeting.qualified_meeting_date ? new Date(localMeeting.qualified_meeting_date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              handleFieldUpdate('qualified_meeting_date', date.toISOString())
                            }
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>

              {localMeeting.lead_status && (
                <EditableField
                  label="Lead Status"
                  value={localMeeting.lead_status}
                  onSave={(value) => handleFieldUpdate('lead_status', value)}
                />
              )}
            </CardContent>
          </Card>

          {/* Notes & Screenshots Section */}
          <MeetingNotesScreenshots 
            meetingId={localMeeting.id}
          />

          {/* CRM Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-4 h-4" />
                CRM & System Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="justify-start"
                  onClick={() => window.open(`https://app.smartlead.ai/app/all-campaigns`, '_blank')}
                >
                  <ExternalLink className="w-3 h-3 mr-2" />
                  Smartlead
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="justify-start"
                  onClick={() => {
                    if (localMeeting.sf_account_id) {
                      window.open(`https://your-salesforce-instance.lightning.force.com/lightning/r/Account/${localMeeting.sf_account_id}/view`, '_blank')
                    } else {
                      toast({
                        title: "No Salesforce ID",
                        description: "Please add a Salesforce Account ID first.",
                        variant: "destructive"
                      })
                    }
                  }}
                >
                  <ExternalLink className="w-3 h-3 mr-2" />
                  Client CRM
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="justify-start"
                  onClick={() => window.open(`/comprehensive-dashboard`, '_blank')}
                >
                  <ExternalLink className="w-3 h-3 mr-2" />
                  OutboundBuddy CRM
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Campaign Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <EditableField
                label="Campaign Name"
                value={localMeeting.campaign_name || ''}
                onSave={(value) => handleFieldUpdate('campaign_name', value)}
              />

              {localMeeting.email_sent_time && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email Sent Time</label>
                  <p className="text-sm mt-1">{formatDate(localMeeting.email_sent_time)}</p>
                </div>
              )}

              {localMeeting.op_created_date && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Opportunity Created</label>
                  <p className="text-sm mt-1">{formatDate(localMeeting.op_created_date)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* External References */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                External References
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <EditableField
                label="Salesforce Account ID"
                value={localMeeting.sf_account_id || ''}
                onSave={(value) => handleFieldUpdate('sf_account_id', value)}
              />

              <div>
                <label className="text-sm font-medium text-muted-foreground">Internal ID</label>
                <p className="text-sm mt-1 font-mono">{localMeeting.id}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Client ID</label>
                <p className="text-sm mt-1">{localMeeting.client_id}</p>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="bg-muted/20">
            <CardHeader>
              <CardTitle className="text-sm text-muted-foreground">Record Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium">Created:</span> {formatDate(localMeeting.created_at)}
                </div>
                <div>
                  <span className="font-medium">Last Updated:</span> {formatDate(localMeeting.updated_at)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  )
}